<script setup>
// Multi-seleção com a identidade visual do Chatwoot (irmão do KanbanSelect, mas
// permite selecionar vários valores). Trigger no estilo do components-next/select
// e lista flutuante com checkboxes. v-model = array de valores.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
    validator: opts =>
      opts.every(o => typeof o === 'object' && 'value' in o && 'label' in o),
  },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  searchable: { type: Boolean, default: true },
  // 'bottom' (padrão) abre pra baixo; 'top' abre pra cima (útil em modais com overflow)
  align: { type: String, default: 'bottom' },
});

const { t } = useI18n();
const modelValue = defineModel({ type: Array, default: () => [] });

const isOpen = ref(false);
const query = ref('');

const sameValue = (a, b) => String(a) === String(b);
const isSelected = v => modelValue.value.some(x => sameValue(x, v));
const selectedOptions = computed(() =>
  props.options.filter(o => isSelected(o.value))
);
const filteredOptions = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return props.options;
  return props.options.filter(o => o.label.toLowerCase().includes(q));
});
const triggerLabel = computed(() => {
  const n = selectedOptions.value.length;
  if (!n) return props.placeholder;
  if (n === 1) return selectedOptions.value[0].label;
  return t('KANBAN.funnelManagement.usersSelected', { count: n });
});

const toggle = () => {
  if (!props.disabled) isOpen.value = !isOpen.value;
};
const choose = option => {
  if (option.disabled) return;
  if (isSelected(option.value)) {
    modelValue.value = modelValue.value.filter(
      x => !sameValue(x, option.value)
    );
  } else {
    modelValue.value = [...modelValue.value, option.value];
  }
};
const removeChip = value => {
  modelValue.value = modelValue.value.filter(x => !sameValue(x, value));
};
</script>

<template>
  <div class="w-full">
    <div v-on-clickaway="() => (isOpen = false)" class="relative w-full">
      <button
        type="button"
        :disabled="disabled"
        class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm text-left transition-all duration-200 rounded-lg outline outline-1 -outline-offset-1 bg-n-alpha-1 text-n-slate-12 disabled:opacity-60 disabled:cursor-not-allowed"
        :class="
          isOpen ? 'outline-n-blue-9' : 'outline-n-weak hover:outline-n-slate-6'
        "
        @click="toggle"
      >
        <span
          class="truncate"
          :class="{ 'text-n-slate-10': !selectedOptions.length }"
        >
          {{ triggerLabel }}
        </span>
        <Icon
          icon="i-lucide-chevron-down"
          class="transition-transform shrink-0 size-4 text-n-slate-11"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>

      <div
        v-if="isOpen"
        class="absolute left-0 right-0 z-40 overflow-hidden border shadow-sm bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border-n-weak"
        :class="align === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'"
      >
        <div v-if="searchable" class="p-2 border-b border-n-weak">
          <input
            v-model="query"
            :placeholder="t('KANBAN.funnelManagement.searchUserPlaceholder')"
            class="reset-base w-full px-2.5 py-1.5 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-background text-n-slate-12"
          />
        </div>
        <ul
          class="grid gap-1 px-2 py-2 overflow-y-auto text-sm list-none max-h-52"
        >
          <li v-for="option in filteredOptions" :key="String(option.value)">
            <button
              type="button"
              :disabled="option.disabled"
              class="flex items-center w-full gap-2.5 px-2.5 py-1.5 text-left rounded-lg text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="choose(option)"
            >
              <span
                class="grid w-4 h-4 border rounded place-items-center shrink-0 transition-colors"
                :class="
                  isSelected(option.value)
                    ? 'bg-n-brand border-n-brand'
                    : 'border-n-strong'
                "
              >
                <Icon
                  v-if="isSelected(option.value)"
                  icon="i-lucide-check"
                  class="text-white size-3"
                />
              </span>
              <span class="truncate">{{ option.label }}</span>
            </button>
          </li>
          <li
            v-if="!filteredOptions.length"
            class="px-2.5 py-2 text-xs text-center text-n-slate-10"
          >
            {{ t('KANBAN.funnelManagement.noAgentsFound') }}
          </li>
        </ul>
      </div>
    </div>

    <!-- chips dos selecionados -->
    <div v-if="selectedOptions.length" class="flex flex-wrap gap-1.5 mt-2">
      <span
        v-for="o in selectedOptions"
        :key="String(o.value)"
        class="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-12"
      >
        {{ o.label }}
        <button
          class="grid w-4 h-4 rounded-full place-items-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
          @click="removeChip(o.value)"
        >
          <Icon icon="i-lucide-x" class="size-3" />
        </button>
      </span>
    </div>
  </div>
</template>
